/**
 * CL-TRADES-SUPPLIER-COLLAB-001B — Project supplier permission editor (pure).
 */

export const PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS = Object.freeze([
  "canViewProjectSuppliers",
  "canManageProjectSuppliers",
]);

export const CLIENT_SUPPLIER_MODULE_PERMISSION_KEY = "canViewSuppliersAndSelections";

export const CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY = "canContributeSupplierInteractions";

export const CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY = "canRespondToProductSelections";

export const FULL_PROJECT_SUPPLIER_ACCESS_CONTROL_KEY = "fullProjectSupplierAccess";

export const PROJECT_SUPPLIER_PERMISSION_CONTROL_LABELS = Object.freeze({
  [FULL_PROJECT_SUPPLIER_ACCESS_CONTROL_KEY]:
    "Full project supplier access (staff convenience only)",
  canViewProjectSuppliers: "View project suppliers",
  canManageProjectSuppliers: "Manage project suppliers and client sharing",
});

export const PROJECT_SUPPLIER_STAFF_DIRECTORY_HELP =
  "Creating and editing reusable companies requires Manage company directory in Staff Access.";

export const PROJECT_SUPPLIER_PROJECT_SCOPE_HELP =
  "Project supplier access is granted separately for each project.";

export const CLIENT_SUPPLIER_MODULE_PERMISSION_LABEL =
  "Show published trades, suppliers and product selections in client portal";

export const CLIENT_SUPPLIER_MODULE_PERMISSION_PORTAL_LABEL =
  "Show published trades, suppliers and product selections in client portal";

/** Permission Center + collaborator editor checkbox (CL-SUPPLIER-COMMUNICATION-006B2). */
export const CLIENT_SUPPLIER_MODULE_PERMISSION_CENTER_LABEL = "View shared suppliers";

export const CLIENT_SUPPLIER_MODULE_PERMISSION_HELP =
  "Opens the Trades & suppliers section in this person's client portal. They still only see suppliers you publish on this project.";

export const CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_LABEL = "Add supplier updates";

export const CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_HELP =
  "Allows this person to record conversations, visits, photos, and documents on suppliers already shared with them. Turning this on also enables View shared suppliers.";

export const CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_LABEL = "Approve or reject product options";

export const CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_HELP =
  "Allows this person to approve or reject published product options in Trades & suppliers. Turning this on also enables View shared suppliers.";

export const SUPPLIER_SHARE_EDITOR_ACCESS_GUIDANCE =
  "Permission Center (Project permissions above) controls whether this person can open Trades & suppliers and add updates. This Trades workspace controls which individual suppliers are published to the client portal.";

export const SUPPLIER_SHARE_EDITOR_PERMISSION_CENTER_HINT =
  "To change module access, open Project permissions for this person and use View shared suppliers and Add supplier updates.";

export function buildClientSupplierModuleEffectiveAccessSummary(permissions = {}) {
  const canView = canClientViewProjectSupplierModule(permissions);
  const canContribute = canClientContributeProjectSupplierInteractions(permissions);
  if (!canView && !canContribute) {
    return "Trades & suppliers module: off (no shared suppliers visible in the client portal).";
  }
  if (canView && !canContribute) {
    return "Trades & suppliers module: view shared suppliers only (no Add update in the client portal).";
  }
  if (canView && canContribute) {
    return "Trades & suppliers module: view shared suppliers and add supplier updates.";
  }
  const canDecide = permissions?.[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] === true;
  if (canView && canDecide) {
    return "Trades & suppliers module: view shared suppliers and approve or reject product options.";
  }
  return "Trades & suppliers module: off.";
}

const SUPPLIER_CHILD_PARENTS = Object.freeze([
  {
    child: "canManageProjectSuppliers",
    parents: ["canViewProjectSuppliers"],
  },
]);

function clonePermissions(permissions = {}) {
  return { ...(permissions || {}) };
}

function supplierControlEnabled(permissions = {}, key = "") {
  return permissions?.[key] === true;
}

export function isFullProjectSupplierAccessEnabled(permissions = {}) {
  return PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS.every((key) =>
    supplierControlEnabled(permissions, key)
  );
}

export function listProjectSupplierPermissionControls() {
  return [...PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS];
}

export function isProjectSupplierPermissionControlChecked(key, permissions = {}) {
  if (key === FULL_PROJECT_SUPPLIER_ACCESS_CONTROL_KEY) {
    return isFullProjectSupplierAccessEnabled(permissions);
  }
  if (key === CLIENT_SUPPLIER_MODULE_PERMISSION_KEY) {
    return supplierControlEnabled(permissions, key);
  }
  if (key === CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY) {
    return supplierControlEnabled(permissions, key);
  }
  if (key === CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY) {
    return supplierControlEnabled(permissions, key);
  }
  return supplierControlEnabled(permissions, key);
}

export function applyDeliberateProjectSupplierPermissionEdit({
  permissions = {},
  key = "",
  checked = false,
} = {}) {
  const next = clonePermissions(permissions);
  if (key === FULL_PROJECT_SUPPLIER_ACCESS_CONTROL_KEY) {
    PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS.forEach((controlKey) => {
      next[controlKey] = Boolean(checked);
    });
    return next;
  }
  if (key === CLIENT_SUPPLIER_MODULE_PERMISSION_KEY) {
    next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] = Boolean(checked);
    if (!checked) {
      next[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] = false;
      next[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] = false;
    }
    return next;
  }
  if (key === CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY) {
    next[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] = Boolean(checked);
    if (checked) next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] = true;
    return next;
  }
  if (key === CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY) {
    next[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] = Boolean(checked);
    if (checked) next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] = true;
    return next;
  }
  if (!PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS.includes(key)) return next;
  next[key] = Boolean(checked);
  if (checked) {
    SUPPLIER_CHILD_PARENTS.forEach(({ child, parents }) => {
      if (child !== key && !next[child]) return;
      if (child === key || next[child] === true) {
        next[child] = true;
        parents.forEach((parent) => {
          next[parent] = true;
        });
      }
    });
    SUPPLIER_CHILD_PARENTS.forEach(({ child, parents }) => {
      if (next[child] === true) {
        parents.forEach((parent) => {
          next[parent] = true;
        });
      }
    });
  } else if (key === "canViewProjectSuppliers") {
    next.canManageProjectSuppliers = false;
  }
  return next;
}

export function normalizeProjectSupplierPermissionsForAssignmentSave(
  permissions = {},
  { rawPermissions = null } = {}
) {
  const source = rawPermissions && typeof rawPermissions === "object" ? rawPermissions : permissions;
  const next = clonePermissions(permissions);
  PROJECT_SUPPLIER_PERMISSION_CONTROL_KEYS.forEach((key) => {
    if (typeof source?.[key] === "boolean") next[key] = source[key] === true;
  });
  if (typeof source?.[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] === "boolean") {
    next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] =
      source[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] === true;
  }
  if (typeof source?.[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] === "boolean") {
    next[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] =
      source[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] === true;
  }
  if (typeof source?.[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] === "boolean") {
    next[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] =
      source[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] === true;
  }
  if (next[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] === true) {
    next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] = true;
  }
  if (next[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] === true) {
    next[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] = true;
  }
  if (next.canManageProjectSuppliers === true) next.canViewProjectSuppliers = true;
  return next;
}

export function canManageProjectSupplierShares(permissions = {}, { isAdmin = false } = {}) {
  return isAdmin || permissions?.canManageProjectSuppliers === true;
}

export function canViewProjectSupplierShares(permissions = {}, { isAdmin = false } = {}) {
  return (
    isAdmin ||
    permissions?.canManageProjectSuppliers === true ||
    permissions?.canViewProjectSuppliers === true
  );
}

export function canClientViewProjectSupplierModule(permissions = {}) {
  return permissions?.[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] === true;
}

export function canClientContributeProjectSupplierInteractions(permissions = {}) {
  return (
    canClientViewProjectSupplierModule(permissions) &&
    permissions?.[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] === true
  );
}

export function canClientRespondToProductSelections(permissions = {}) {
  return (
    canClientViewProjectSupplierModule(permissions) &&
    permissions?.[CLIENT_SUPPLIER_PRODUCT_DECISION_PERMISSION_KEY] === true
  );
}
